import React, { useState } from "react";
import { Box, HStack, Image, RatingGroup, Stack, Text } from "@chakra-ui/react";
import { MdAutoAwesome, MdRefresh } from "react-icons/md";

const CATALOG = [
  {
    id: "bowling",
    nombre: "Bowling",
    rating: 5,
    foto: "https://kingpin.mx/images/home-kingpin-oct-2025-xl.jpg",
    tipo: ["social"],
    ambiente: ["techo"],
  },
  {
    id: "bicis",
    nombre: "Bicis en la Huasteca",
    rating: 5,
    foto: "https://i0.wp.com/foodandpleasure.com/wp-content/uploads/2022/12/la-huasteca-monterrey-shutter-1.jpg?fit=1280%2C959&ssl=1",
    tipo: ["aventura"],
    ambiente: ["aire-libre"],
  },
  {
    id: "jumpster",
    nombre: "Jumpster",
    rating: 4.5,
    foto: "https://www.skyzone.com/wp-content/uploads/2022/09/about_hero.jpg",
    tipo: ["aventura", "social"],
    ambiente: ["techo"],
  },
  {
    id: "esports",
    nombre: "eSports",
    rating: 4.5,
    foto: "https://weezevent.com/wp-content/uploads/2018/10/15140712/compet_esport.jpg",
    tipo: ["deporte", "social"],
    ambiente: ["techo"],
  },
  {
    id: "tee",
    nombre: "Tee de Práctica",
    rating: 4.5,
    foto: "https://campestreags.com/wp-content/uploads/2020/12/galeria-inst-tee-practica-02.jpg",
    tipo: ["deporte"],
    ambiente: ["aire-libre"],
  },
  {
    id: "beisbol",
    nombre: "Beisbol",
    rating: 4,
    foto: "https://www.beisbolmlb.com/wp-content/uploads/beisbolvenezolano4-e1501176946770.jpg",
    tipo: ["deporte"],
    ambiente: ["aire-libre"],
  },
  {
    id: "deportes",
    nombre: "Deportes",
    rating: 4,
    foto: "https://www.unila.edu.mx/wp-content/uploads/2025/09/los-5-deportes-mas-populares-del-mundo.jpg",
    tipo: ["deporte", "aventura"],
    ambiente: ["aire-libre", "techo"],
  },
  {
    id: "zoologico",
    nombre: "Zoológico",
    rating: 3.5,
    foto: "https://nltravel.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/05/23132645/IMG_0392-1-scaled.jpg",
    tipo: ["aventura", "cultura"],
    ambiente: ["aire-libre"],
  },
];

const QUESTIONS = [
  {
    id: "tipo",
    text: "¿Qué tipo de actividad?",
    options: [
      { id: "deporte",  label: "Deporte",   emoji: "⚽" },
      { id: "aventura", label: "Aventura",  emoji: "⛰️" },
      { id: "social",   label: "Social",    emoji: "🤝" },
      { id: "cultura",  label: "Cultura",   emoji: "🎨" },
    ],
  },
  {
    id: "ambiente",
    text: "¿Dónde prefieren estar?",
    options: [
      { id: "aire-libre", label: "Al aire libre", emoji: "🌿" },
      { id: "techo",      label: "Bajo techo",    emoji: "🏠" },
    ],
  },
];

function scoreActivity(activity, answers) {
  let score = 0;
  const { tipo, ambiente } = answers;
  if (tipo && activity.tipo.includes(tipo)) score += 3;
  if (ambiente && activity.ambiente.includes(ambiente)) score += 2;
  score += activity.rating / 10;
  return score;
}

function getRecommendations(answers) {
  return [...CATALOG]
    .map((a) => ({ ...a, _score: scoreActivity(a, answers) }))
    .sort((a, b) => b._score - a._score)
    .slice(0, 3);
}

export function RecomendacionesPanel() {
  const [step, setStep] = useState("idle");
  const [answers, setAnswers] = useState({});

  const qIndex = step.startsWith("q") ? Number(step.slice(1)) : null;
  const currentQ = qIndex !== null ? QUESTIONS[qIndex] : null;

  function startQuiz() {
    setAnswers({});
    setStep("q0");
  }

  function handleAnswer(questionId, optionId) {
    const next = { ...answers, [questionId]: optionId };
    setAnswers(next);
    const nextIdx = qIndex + 1;
    setStep(nextIdx < QUESTIONS.length ? `q${nextIdx}` : "results");
  }

  function reset() {
    setAnswers({});
    setStep("idle");
  }

  const results = step === "results" ? getRecommendations(answers) : [];

  return (
    <Box
      bg="dashboard.bg.surface"
      rounded="card"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      minH={{ base: "auto", lg: "300px" }}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <HStack
        px="5"
        pt="4"
        pb="3"
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="dashboard.border.muted"
      >
        <Text
          fontSize="10px"
          fontWeight="800"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color="dashboard.fg.subtle"
        >
          Recomendaciones
        </Text>
        {step !== "idle" && (
          <Box
            as="button"
            onClick={reset}
            display="grid"
            placeItems="center"
            boxSize="5"
            rounded="md"
            color="dashboard.fg.muted"
            _hover={{ color: "dashboard.fg.default", bg: "dashboard.bg.surfaceMuted" }}
            transition="all 150ms ease"
            aria-label="Reiniciar"
          >
            <MdRefresh size="13px" />
          </Box>
        )}
      </HStack>

      {/* Body */}
      <Box px="5" py="5" flex="1" display="flex" flexDirection="column">

        {/* ── Idle ── */}
        {step === "idle" && (
          <Stack gap="4" flex="1" justify="center">
            <Box
              display="grid"
              placeItems="center"
              boxSize="9"
              rounded="xl"
              bg="red.100"
              color="red.600"
              _dark={{ bg: "red.800", color: "red.200" }}
            >
              <MdAutoAwesome size="18px" />
            </Box>
            <Stack gap="1">
              <Text fontWeight="700" fontSize="sm" color="dashboard.fg.default">
                ¿Qué actividad hacer?
              </Text>
              <Text fontSize="xs" color="dashboard.fg.muted" lineHeight="1.6">
                Responde 2 preguntas y encontramos las mejores opciones para tu grupo.
              </Text>
            </Stack>
            <Box
              as="button"
              onClick={startQuiz}
              alignSelf="start"
              px="3"
              py="1.5"
              rounded="lg"
              bg="red.500"
              color="white"
              fontSize="xs"
              fontWeight="700"
              _hover={{ bg: "red.600" }}
              transition="background 150ms ease"
              cursor="pointer"
            >
              Descubrir actividades
            </Box>
          </Stack>
        )}

        {/* ── Quiz ── */}
        {currentQ && (
          <Stack gap="4">
            {/* Progress */}
            <HStack gap="1.5">
              {QUESTIONS.map((_, i) => (
                <Box
                  key={i}
                  h="2px"
                  flex="1"
                  rounded="full"
                  bg={i <= qIndex ? "red.500" : "dashboard.border.muted"}
                  transition="background 200ms ease"
                />
              ))}
            </HStack>

            <Stack gap="0.5">
              <Text
                fontSize="xs"
                color="dashboard.fg.muted"
                textTransform="uppercase"
                fontWeight="700"
                letterSpacing="0.06em"
              >
                {qIndex + 1} / {QUESTIONS.length}
              </Text>
              <Text fontWeight="700" fontSize="sm" color="dashboard.fg.default">
                {currentQ.text}
              </Text>
            </Stack>

            <Stack gap="2">
              {currentQ.options.map((opt) => (
                <Box
                  key={opt.id}
                  as="button"
                  w="full"
                  onClick={() => handleAnswer(currentQ.id, opt.id)}
                  px="3"
                  py="2.5"
                  rounded="lg"
                  borderWidth="1px"
                  borderColor="dashboard.border.muted"
                  bg="transparent"
                  display="flex"
                  alignItems="center"
                  gap="2.5"
                  _hover={{ bg: "dashboard.bg.surfaceMuted", borderColor: "red.400" }}
                  _dark={{ _hover: { borderColor: "red.600" } }}
                  transition="all 150ms ease"
                  cursor="pointer"
                  textAlign="left"
                >
                  <Text fontSize="lg" lineHeight="1" flexShrink={0}>{opt.emoji}</Text>
                  <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">{opt.label}</Text>
                </Box>
              ))}
            </Stack>
          </Stack>
        )}

        {/* ── Results ── */}
        {step === "results" && (
          <Stack gap="3">
            <Text
              fontSize="xs"
              color="dashboard.fg.muted"
              textTransform="uppercase"
              fontWeight="700"
              letterSpacing="0.06em"
            >
              Para tu grupo
            </Text>
            {results.map((act) => (
              <HStack key={act.id} gap="3">
                <Image
                  src={act.foto}
                  alt={act.nombre}
                  boxSize="44px"
                  flexShrink={0}
                  objectFit="cover"
                  rounded="lg"
                />
                <Box flex="1" minW="0">
                  <Text
                    fontFamily="display"
                    fontWeight="700"
                    fontSize="sm"
                    color="dashboard.fg.default"
                    lineClamp={1}
                    lineHeight="1.3"
                  >
                    {act.nombre}
                  </Text>
                  <RatingGroup.Root readOnly value={act.rating} size="sm">
                    <RatingGroup.HiddenInput />
                    <RatingGroup.Control />
                  </RatingGroup.Root>
                </Box>
              </HStack>
            ))}
          </Stack>
        )}

      </Box>
    </Box>
  );
}
